/**
 * Autocomplete for refs while typing (SPEC §6.2): `[[` pages, `#` tags, `@` people, `((` blocks.
 * Detection looks only at the text before the caret, so it costs a few string scans per keystroke
 * and nothing at all when no trigger is near.
 */
import { isPerson, normalizeTitle, plainText, titleKey, titleProblem, type Graph, type Node, type NodeId, type TitleIndex } from '@solar/core'

export type PickerKind = 'link' | 'tag' | 'mention' | 'block'

export interface PickerContext {
  readonly kind: PickerKind
  /** Start of the trigger: `[[`, `#`, `@`, `((`, or the `#`/`@` of `#[[` / `@[[`. */
  readonly from: number
  /** End of the text a pick replaces: after the closing `]]`/`))` if there is one, else the caret. */
  readonly to: number
  /** What was typed after the trigger, up to the caret. */
  readonly query: string
  /** `#[[…` / `@[[…`: the pick keeps the bracket form. */
  readonly bracket: boolean
}

export type PickerItem =
  | { readonly type: 'node'; readonly node: Node }
  | { readonly type: 'create'; readonly title: string }
  | { readonly type: 'block'; readonly node: Node }

const TAG_CHAR = /[\p{L}\p{N}\p{M}_\-/+&.]/u
const NAME_CHAR = /[\p{L}\p{N}\p{M}_\-.]/u
const SPACE = /\s/u

/** Same rule as the parser: `#`/`@` start a ref at the start or after whitespace, an opener or emphasis. */
function boundary(text: string, i: number): boolean {
  return i === 0 || SPACE.test(text[i - 1]!) || '([{"\'*_~='.includes(text[i - 1]!)
}

/** The ref being typed at the caret, or null. */
export function detect(text: string, caret: number): PickerContext | null {
  for (const [open, close, kind] of [
    ['[[', ']]', 'link'],
    ['((', '))', 'block'],
  ] as const) {
    const i = caret >= 2 ? text.lastIndexOf(open, caret - 2) : -1
    if (i < 0) continue
    const query = text.slice(i + 2, caret)
    if (/[[\]\n]/.test(query) || (kind === 'block' && /[()]/.test(query))) continue
    const j = text.indexOf(close, caret)
    const tail = j < 0 ? '' : text.slice(caret, j)
    const to = j >= 0 && !/[[\]()\n]/.test(tail) ? j + 2 : caret
    if (kind === 'link' && i > 0 && (text[i - 1] === '#' || text[i - 1] === '@') && boundary(text, i - 1)) {
      return { kind: text[i - 1] === '#' ? 'tag' : 'mention', from: i - 1, to, query, bracket: true }
    }
    return { kind, from: i, to, query, bracket: true }
  }
  for (const [sign, chars, kind] of [
    ['#', TAG_CHAR, 'tag'],
    ['@', NAME_CHAR, 'mention'],
  ] as const) {
    let k = caret
    while (k > 0 && chars.test(text[k - 1]!)) k--
    if (k === 0 || text[k - 1] !== sign || !boundary(text, k - 1)) continue
    let to = caret
    while (to < text.length && chars.test(text[to]!)) to++
    return { kind, from: k - 1, to, query: text.slice(k, caret), bracket: false }
  }
  return null
}

function subsequence(q: string, s: string): boolean {
  let i = 0
  for (const c of s) if (c === q[i] && ++i === q.length) return true
  return q.length === 0
}

/**
 * Titled nodes for a query: exact title, then prefix, word prefix, substring, fuzzy subsequence;
 * recently updated first within a rank.
 */
export function searchTitles(titles: TitleIndex, query: string, limit = 8): Node[] {
  const q = titleKey(query)
  const scored: { n: Node; s: number }[] = []
  for (const n of titles.nodes()) {
    const k = titleKey(n.title!)
    let s: number
    if (q === '') s = 1
    else if (k === q) s = 100
    else if (k.startsWith(q)) s = 80
    else if (k.split(/[\s/_.-]+/).some((w) => w.startsWith(q))) s = 60
    else if (k.includes(q)) s = 40
    else if (subsequence(q, k)) s = 20
    else continue
    scored.push({ n, s })
  }
  scored.sort((a, b) => b.s - a.s || b.n.updatedAt - a.n.updatedAt || a.n.title!.length - b.n.title!.length)
  return scored.slice(0, limit).map((x) => x.n)
}

/**
 * Blocks containing every word of the query (case-insensitive), most recently updated first.
 * A linear scan of the mirror; the search worker (M5) replaces it.
 */
export function searchBlocks(g: Graph, query: string, exclude: NodeId, limit = 8): Node[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return []
  const hits: Node[] = []
  for (const n of g.nodes()) {
    if (n.kind !== 'block' || n.deletedAt !== null || n.id === exclude || n.content === '') continue
    const text = n.content.toLowerCase()
    if (words.every((w) => text.includes(w))) hits.push(n)
  }
  hits.sort((a, b) => b.updatedAt - a.updatedAt)
  return hits.slice(0, limit)
}

/**
 * Items for a context: matches, then "Create" when the query is a new, valid title. `@` lists
 * people only (D13); its "Create" makes a person.
 */
export function pickerItems(g: Graph, titles: TitleIndex, ctx: PickerContext, blockId: string): PickerItem[] {
  if (ctx.kind === 'block') return searchBlocks(g, ctx.query, blockId).map((node) => ({ type: 'block', node }))
  const found = ctx.kind === 'mention' ? searchTitles(titles, ctx.query, 200).filter((n) => isPerson(g, n)).slice(0, 8) : searchTitles(titles, ctx.query)
  const items: PickerItem[] = found.map((node) => ({ type: 'node', node }))
  const title = normalizeTitle(ctx.query)
  if (title !== '' && titleProblem(title) === null && titles.resolve(title) === null) items.push({ type: 'create', title })
  return items
}

/** One line of a block for the block picker. */
export function blockLine(n: Node): string {
  const t = plainText(n.content)
  return t.length > 80 ? `${t.slice(0, 79)}…` : t
}
